import React, { useState, useEffect, useCallback } from 'react';
import { TrendingUp, TrendingDown, Minus, Users, BarChart2, RefreshCw, ArrowRight, AlertCircle, Calculator, Sparkles } from 'lucide-react';
import { GlassCard, GlassBadge, GlassButton } from '../ui';
import { getPredictionDashboard } from '../../services/api';
import { useNavigate } from 'react-router-dom';

interface PredictionDashboardWidgetProps {
    projectId: string;
    /** When true, renders in compact mode for embedding in the main Dashboard */
    compact?: boolean;
}

interface DashboardPrediction {
    metric: string;
    direction: 'up' | 'down' | 'stable';
    summary: string;
    confidence: number;
    method?: 'holt_linear' | 'llm_heuristic';
}

function getMethodIcon(method?: string) {
    if (method === 'holt_linear') {
        return (
            <span title="Computed forecast" style={{ display: 'inline-flex', flexShrink: 0 }}>
                <Calculator size={12} style={{ color: 'var(--success)' }} />
            </span>
        );
    }
    if (method === 'llm_heuristic') {
        return (
            <span title="AI estimate" style={{ display: 'inline-flex', flexShrink: 0 }}>
                <Sparkles size={12} style={{ color: 'var(--text-secondary)' }} />
            </span>
        );
    }
    return null;
}

interface HotLead {
    email: string;
    score: number;
    action: string;
}

interface TrendMetric {
    metric: string;
    direction: 'up' | 'down' | 'stable';
    change_pct: number;
}

interface DashboardData {
    predictions: DashboardPrediction[];
    hot_leads: HotLead[];
    trend_metrics: TrendMetric[];
    generated_at: string;
}

function getTrendIcon(direction: string, size: number = 14) {
    switch (direction) {
        case 'up': return <TrendingUp size={size} style={{ color: 'var(--success)' }} />;
        case 'down': return <TrendingDown size={size} style={{ color: 'var(--danger)' }} />;
        default: return <Minus size={size} style={{ color: 'var(--text-secondary)' }} />;
    }
}

const PredictionDashboardWidget: React.FC<PredictionDashboardWidgetProps> = ({ projectId, compact = false }) => {
    const navigate = useNavigate();
    const [data, setData] = useState<DashboardData | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchDashboard = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await getPredictionDashboard(projectId);
            setData(result);
        } catch (err: any) {
            setError(err?.response?.data?.detail || 'Failed to load predictions');
        } finally {
            setLoading(false);
        }
    }, [projectId]);

    useEffect(() => {
        fetchDashboard();
    }, [fetchDashboard]);

    if (loading) {
        return (
            <GlassCard padding="md" className="prediction-widget">
                <div className="prediction-widget-header">
                    <BarChart2 size={18} style={{ color: 'var(--primary-500)' }} />
                    <h4 className="prediction-widget-title">Predictive Analytics</h4>
                </div>
                <div className="prediction-widget-loading">
                    <RefreshCw size={18} className="trend-spinner" style={{ color: 'var(--primary-500)' }} />
                    <span style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)' }}>Loading...</span>
                </div>
            </GlassCard>
        );
    }

    if (error) {
        return (
            <GlassCard padding="md" className="prediction-widget">
                <div className="prediction-widget-header">
                    <BarChart2 size={18} style={{ color: 'var(--primary-500)' }} />
                    <h4 className="prediction-widget-title">Predictive Analytics</h4>
                </div>
                <div className="prediction-widget-error">
                    <AlertCircle size={16} style={{ color: 'var(--danger)' }} />
                    <span style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)' }}>{error}</span>
                </div>
            </GlassCard>
        );
    }

    if (!data) {
        return (
            <GlassCard padding="md" className="prediction-widget">
                <div className="prediction-widget-header">
                    <BarChart2 size={18} style={{ color: 'var(--primary-500)' }} />
                    <h4 className="prediction-widget-title">Predictive Analytics</h4>
                </div>
                <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-sm)', margin: 0 }}>
                    No prediction data available yet.
                </p>
            </GlassCard>
        );
    }

    const topPredictions = (data.predictions || []).slice(0, 3);
    const topLeads = (data.hot_leads || []).slice(0, 3);
    const trendMetrics = (data.trend_metrics || []).slice(0, 4);

    return (
        <GlassCard padding="md" className="prediction-widget">
            <div className="prediction-widget-header">
                <BarChart2 size={18} style={{ color: 'var(--primary-500)' }} />
                <h4 className="prediction-widget-title">Predictive Analytics</h4>
                {!compact && (
                    <GlassButton
                        variant="ghost"
                        size="sm"
                        icon={<RefreshCw size={14} />}
                        onClick={fetchDashboard}
                        title="Refresh predictions"
                    />
                )}
            </div>

            {/* Trend Metrics */}
            {trendMetrics.length > 0 && (
                <div className="prediction-widget-trends">
                    {trendMetrics.map((tm, idx) => (
                        <div key={idx} className="prediction-widget-trend-item">
                            {getTrendIcon(tm.direction)}
                            <span className="prediction-widget-trend-metric">{tm.metric}</span>
                            <span className={`prediction-widget-trend-pct ${tm.direction === 'up' ? 'pct-up' : tm.direction === 'down' ? 'pct-down' : ''}`}>
                                {tm.direction === 'up' ? '+' : ''}{tm.change_pct}%
                            </span>
                        </div>
                    ))}
                </div>
            )}

            {/* Top Predictions */}
            {topPredictions.length > 0 && (
                <div className="prediction-widget-section">
                    <h5 className="prediction-widget-section-title">Top Predictions</h5>
                    {topPredictions.map((pred, idx) => (
                        <div key={idx} className="prediction-widget-prediction-row">
                            {getTrendIcon(pred.direction)}
                            <span className="prediction-widget-prediction-text">{pred.summary}</span>
                            {getMethodIcon(pred.method)}
                        </div>
                    ))}
                </div>
            )}

            {/* Hot Leads */}
            {topLeads.length > 0 && (
                <div className="prediction-widget-section">
                    <h5 className="prediction-widget-section-title">
                        <Users size={14} style={{ color: 'var(--primary-500)' }} />
                        Hot Leads
                    </h5>
                    {topLeads.map((lead, idx) => (
                        <div key={idx} className="prediction-widget-lead-row">
                            <span className="prediction-widget-lead-email">{lead.email}</span>
                            <GlassBadge
                                variant={lead.score >= 61 ? 'success' : lead.score >= 31 ? 'warning' : 'danger'}
                                size="sm"
                            >
                                {lead.score}
                            </GlassBadge>
                        </div>
                    ))}
                </div>
            )}

            {/* View All Link */}
            <div className="prediction-widget-footer">
                <GlassButton
                    variant="secondary"
                    size="sm"
                    icon={<ArrowRight size={14} />}
                    iconPosition="right"
                    onClick={() => navigate('/predictions')}
                >
                    View All
                </GlassButton>
            </div>
        </GlassCard>
    );
};

export default PredictionDashboardWidget;
